import {Box, ButtonBase, IconButton, TableCell, TableRow, Tooltip, Typography, useTheme} from "@mui/material";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import {useTranslation} from "react-i18next";
import QuantitySelector from "./quantity-selector";
import AppliedDiscount from "./common/applied-discount";
import {appliedCartDiscount} from "./common/cart-pricing";

function BasketProductName({item, onDetails}) {
    const {t} = useTranslation();
    const nameStyle = {display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        overflow: "hidden", overflowWrap: "anywhere", textAlign: "left"};
    return <Tooltip title={item.productName} placement="top-start" arrow>
        {item.details ? <ButtonBase onClick={() => onDetails(item.details)} aria-label={t("BasketViewProduct", {name: item.productName})}
            sx={{width: "100%", minWidth: 0, justifyContent: "flex-start", borderRadius: 0.5,
                "&:hover .MuiTypography-root": {textDecoration: "underline"},
                "&.Mui-focusVisible": {outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2}}}>
            <Typography component="span" variant="body2" sx={nameStyle}>{item.productName}</Typography>
        </ButtonBase> : <Typography component="span" variant="body2" tabIndex={0} sx={nameStyle}>{item.productName}</Typography>}
    </Tooltip>;
}

function BasketProductPrice({item}) {
    const {t} = useTranslation();
    const discount = appliedCartDiscount(item);
    // Keep the discount explanation accessible without letting badges change row height.
    return <Tooltip title={discount > 0 ? t("BasketAppliedDiscount", {percent: discount}) : ""} describeChild arrow>
        <Box tabIndex={discount > 0 ? 0 : undefined} sx={{"& .MuiTypography-root": {overflowWrap: "anywhere"},
            "&:focus-visible": {outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2}}}>
            <AppliedDiscount product={{...item, price: item.originalPrice ?? item.price}} showBadge={false}/>
        </Box>
    </Tooltip>;
}

export default function BasketProductRow({item, quantity, onChange, onBlur, onRemove, onDetails}) {
    const {t} = useTranslation();
    const theme = useTheme();
    const divider = `1px solid ${theme.palette.divider}`;
    const cellStyle = {display: {xs: "block", sm: "table-cell"}, minWidth: 0,
        borderBottom: {xs: 0, sm: divider}, p: {xs: 1, sm: 1.5}};
    // Stack name/actions above price/quantity on phones while keeping the familiar desktop columns.
    return <TableRow sx={{display: {xs: "grid", sm: "table-row"}, gridTemplateColumns: "minmax(0, 1fr) auto",
        gridTemplateAreas: '"product remove" "price quantity"', borderBottom: {xs: divider, sm: 0}}}>
        <TableCell padding="none" sx={{...cellStyle, gridArea: "remove", width: {sm: 44}, px: {xs: 1, sm: 0}, textAlign: "center"}}>
            <Tooltip title={t("BasketRemoveProduct", {name: item.productName})} arrow>
                <IconButton size="small" onClick={() => onRemove(item.cartId)} aria-label={t("BasketRemoveProduct", {name: item.productName})}>
                    <ClearOutlinedIcon fontSize="small"/>
                </IconButton>
            </Tooltip>
        </TableCell>
        <TableCell sx={{...cellStyle, gridArea: "product", pl: {xs: 1, sm: 1}}}><BasketProductName item={item} onDetails={onDetails}/></TableCell>
        <TableCell sx={{...cellStyle, gridArea: "price", width: {sm: 124}, px: {xs: 1, sm: 0.5}}}>
            <Typography variant="caption" color="text.secondary" sx={{display: {xs: "block", sm: "none"}}}>{t("Price")}</Typography>
            <BasketProductPrice item={item}/>
        </TableCell>
        <TableCell align="center" sx={{...cellStyle, gridArea: "quantity", width: {sm: 104}, px: {xs: 1, sm: 0}}}>
            <Typography variant="caption" color="text.secondary" sx={{display: {xs: "block", sm: "none"}}}>{t("Quantity")}</Typography>
            <Box sx={{display: "flex", justifyContent: "center"}}><QuantitySelector initialValue={quantity} min={1} max={999} showActionTooltips
                onChange={onChange} onBlur={onBlur} onAddToBasket={onBlur} row={item}
                inputLabel={t("BasketProductQuantity", {name: item.productName})}
                decreaseLabel={t("BasketDecreaseQuantity", {name: item.productName})}
                increaseLabel={t("BasketIncreaseQuantity", {name: item.productName})}/></Box>
        </TableCell>
    </TableRow>;
}
